// UI strings for "our own voices": recording the approved sentence-stack sentences (Lue lauseet
// ääneen, RecordPrompt) and the voice-model consent in Asetukset. Finnish (default) and English
// side by side; en has the same shape. Style: docs/FINNISH-STYLE.md (no internal jargon, sinä-muoto,
// "1 lause", "2 lausetta"). The users' recordings are always "meidän ääntämys", never official.
import { defineArea } from '../area';

const fiNum = (n: number) => n.toLocaleString('fi-FI');
const enNum = (n: number) => n.toLocaleString('en-GB');

export const voiceFi = {
  // ---------- Lue lauseet ääneen (was "Ääneennä lauseet") ----------
  door: 'Lue lauseet ääneen',
  title: 'Lue lauseet ääneen',
  today: (n: number, goal: number) => `Tänään ${fiNum(n)} / ${fiNum(goal)}`,
  goalLabel: 'Päivän tavoite',
  goalDone: 'Päivän tavoite on täynnä. Jatka, jos huvittaa.',
  waiting: (n: number) => (n === 1 ? '1 lause odottaa ääntä' : `${fiNum(n)} lausetta odottaa ääntä`),
  show: 'Näytä',
  filterNone: 'Ei vielä ääntä',
  filterMine: 'Ei vielä minun ääntäni',
  filterNoneHint: 'Lauseet, joita kumpikaan ei ole vielä äänittänyt.',
  filterMineHint: 'Lauseet, joita sinä et ole vielä äänittänyt, vaikka kumppanisi olisi.',
  current: 'Lue ääneen',
  skip: 'Ohita',
  skipped: (n: number) => (n === 1 ? '1 ohitettu tällä kertaa' : `${fiNum(n)} ohitettu tällä kertaa`),
  unskip: 'Palauta ohitetut',
  next: 'Seuraavaksi',
  savedNext: 'Tallennettu. Tässä seuraava.',
  emptyAll: 'Jokaisella hyväksytyllä lauseella on jo ääni.',
  emptyMine: 'Olet äänittänyt kaikki hyväksytyt lauseet.',
  emptySkipped: 'Ohitit loput lauseet. Palauta ne, kun haluat jatkaa.',
  // No approved sentences yet (the stack has drafts only).
  noneTitle: 'Yhtäkään lausetta ei ole vielä hyväksytty',
  noneBody: (drafted: number) =>
    drafted > 0
      ? `Lausepinossa on ${fiNum(drafted)} lauseluonnosta, mutta valyriankielisiä versioita ei ole vielä hyväksytty. Kun hyväksytte lauseen, se tulee tähän äänitettäväksi.`
      : 'Lausepino on vielä tyhjä. Kun hyväksytte lauseita, ne tulevat tähän äänitettäviksi.',
  loadFailed: 'Lauseita ei saatu ladattua.',
  needsOnline: 'Lauseiden lataaminen vaatii yhteyden, kun avaat tämän ensimmäisen kerran.',
  back: 'Takaisin ääntämiseen',

  // ---------- RecordPrompt: one button, two takes, keep the better ----------
  prompt: {
    record: 'Äänitä',
    again: 'Äänitä toinen otto',
    stop: 'Lopeta',
    starting: 'Mikrofoni käynnistyy…',
    recording: 'Äänitetään…',
    hint: (s: number) => `Napauta, lue lause ääneen ja napauta uudelleen. Enintään ${s} sekuntia.`,
    take: (n: number) => `${n}. otto`,
    seconds: (s: number) => `${s.toLocaleString('fi-FI', { maximumFractionDigits: 1 })} s`,
    play: 'Kuuntele',
    pause: 'Tauko',
    pick: 'Kumpi otto tallennetaan?',
    twoTakes: 'Kaksi ottoa riittää. Valitse parempi ja tallenna.',
    save: 'Tallenna',
    saveTake: (n: number) => `Tallenna ${n}. otto`,
    discard: 'Hylkää otot',
    saving: 'Tallennetaan…',
    saved: 'Tallennettu – meidän ääntämys.',
    offline: 'Tallentaminen vaatii yhteyden. Otot säilyvät tässä, kunnes poistut sivulta.',
    failed: 'Tallentaminen ei onnistunut. Yritä uudelleen.',
    demo: 'Demotilassa äänityksiä ei tallenneta palvelimelle.',
  },

  // ---------- Consent (Asetukset) ----------
  consent: {
    title: 'Oma puhemalli',
    question: 'Saako ääntäni käyttää oman puhemallin opettamiseen?',
    no: 'Ei',
    yes: 'Kyllä',
    what: 'Jos sallit, äänityksistäsi voidaan myöhemmin koota aineisto, jolla omalla tietokoneellanne opetetaan valyriaa lukeva puhemalli sinun äänelläsi. Aineisto ei lähde minnekään muualle, eikä mitään asenneta ilman teidän lupaanne.',
    off: 'Ilman lupaa ääntäsi ei käytetä mallin opettamiseen. Äänitykset toimivat silti kuten ennenkin.',
    withdraw: 'Voit perua luvan milloin tahansa. Silloin jo koottu aineisto ja siitä opetettu malli poistetaan.',
    onlyYou: 'Vain sinä voit muuttaa tätä.',
    changed: (when: string) => `Muutettu ${when}.`,
    partnerYes: (name: string) => `${name} on antanut luvan omalle äänelleen.`,
    partnerNo: (name: string) => `${name} ei ole antanut lupaa omalle äänelleen.`,
    saving: 'Tallennetaan…',
    failed: 'Muutos ei tallentunut. Yritä uudelleen.',
    needsOnline: 'Luvan muuttaminen vaatii yhteyden.',
    demo: 'Demotilassa lupaa ei voi muuttaa.',
  },
};

export type VoiceMessages = typeof voiceFi;

export const voiceEn: VoiceMessages = {
  door: 'Voice the sentences',
  title: 'Voice the sentences',
  today: (n, goal) => `Today ${enNum(n)} / ${enNum(goal)}`,
  goalLabel: 'Daily goal',
  goalDone: 'Today’s goal is reached. Carry on if you like.',
  waiting: (n) => (n === 1 ? '1 sentence waiting for a voice' : `${enNum(n)} sentences waiting for a voice`),
  show: 'Show',
  filterNone: 'No voice yet',
  filterMine: 'Not yet in my voice',
  filterNoneHint: 'Sentences neither of you has recorded yet.',
  filterMineHint: 'Sentences you have not recorded yet, even if your partner has.',
  current: 'Read aloud',
  skip: 'Skip',
  skipped: (n) => (n === 1 ? '1 skipped this time' : `${enNum(n)} skipped this time`),
  unskip: 'Bring back skipped',
  next: 'Up next',
  savedNext: 'Saved. Here is the next one.',
  emptyAll: 'Every approved sentence has a voice.',
  emptyMine: 'You have recorded every approved sentence.',
  emptySkipped: 'You skipped the rest. Bring them back when you want to continue.',
  noneTitle: 'No sentence has been approved yet',
  noneBody: (drafted) =>
    drafted > 0
      ? `The sentence stack holds ${enNum(drafted)} draft sentences, but no High Valyrian version has been approved yet. Once you approve a sentence, it appears here to be recorded.`
      : 'The sentence stack is still empty. Once you approve sentences, they appear here to be recorded.',
  loadFailed: 'The sentences could not be loaded.',
  needsOnline: 'Loading the sentences needs a connection the first time you open this.',
  back: 'Back to pronunciation',
  prompt: {
    record: 'Record',
    again: 'Record a second take',
    stop: 'Stop',
    starting: 'Starting the microphone…',
    recording: 'Recording…',
    hint: (s) => `Tap, read the sentence aloud and tap again. Up to ${s} seconds.`,
    take: (n) => `Take ${n}`,
    seconds: (s) => `${s.toLocaleString('en-GB', { maximumFractionDigits: 1 })} s`,
    play: 'Play',
    pause: 'Pause',
    pick: 'Which take should be saved?',
    twoTakes: 'Two takes is enough. Pick the better one and save it.',
    save: 'Save',
    saveTake: (n) => `Save take ${n}`,
    discard: 'Discard takes',
    saving: 'Saving…',
    saved: 'Saved – our pronunciation.',
    offline: 'Saving needs a connection. The takes stay here until you leave the page.',
    failed: 'Saving failed. Try again.',
    demo: 'In demo mode recordings are not saved to the server.',
  },
  consent: {
    title: 'Personal voice model',
    question: 'May my voice be used to train a personal voice model?',
    no: 'No',
    yes: 'Yes',
    what: 'If you allow it, your recordings can later be gathered into a dataset for training, on your own computer, a voice model that reads High Valyrian in your voice. The dataset goes nowhere else, and nothing is installed without your OK.',
    off: 'Without your OK your voice is not used to train anything. Recording works as before.',
    withdraw: 'You can withdraw at any time. The dataset gathered so far and any model trained from it are then deleted.',
    onlyYou: 'Only you can change this.',
    changed: (when) => `Changed ${when}.`,
    partnerYes: (name) => `${name} has allowed it for their own voice.`,
    partnerNo: (name) => `${name} has not allowed it for their own voice.`,
    saving: 'Saving…',
    failed: 'The change was not saved. Try again.',
    needsOnline: 'Changing this needs a connection.',
    demo: 'In demo mode this cannot be changed.',
  },
};

// Messages.voice (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('voice', voiceFi, voiceEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof voiceFi> {}
}
